/**
 * Text Field Component (Email, Name, etc.)
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { formatFromVariableSelect, formatFromVariableText } from '../../format-utils'
import type { SpacingInputValue, TypographyValue, VariableSelectValue, VariableTextValue } from '../../types'
import { deepMerge, parseToVariableSelect, parseToVariableText } from '../../parser-utils'
import textFieldTemplate from './text-field.template.json'

export interface ITextFieldComponentSettings {
  // Data settings
  'text-field-data-value': string // Column ID
  'text-field-data-label': VariableTextValue
  'text-field-data-prefix': VariableTextValue
  'text-field-data-suffix': VariableTextValue
  'text-field-data-help': VariableTextValue
  'text-field-data-default-value': VariableSelectValue
  'text-field-data-default-value-editable': boolean
  'text-field-data-leading-icon': any
  'text-field-data-trailing-icon': any

  // Design settings
  'text-field-design-height': string
  'text-field-design-scheme': any
  'text-field-design-color-variant': string
  'text-field-design-outlined': any
  'text-field-design-margin': SpacingInputValue
  'text-field-design-typography': TypographyValue
  'text-field-design-label-typography': TypographyValue
  'text-field-design-input-typography': TypographyValue
  'text-field-design-help-typography': TypographyValue

  // Behavior settings
  'text-field-behavior-pattern': string
  'text-field-behavior-pattern-show-help': boolean
  'text-field-behavior-min-char': number
  'text-field-behavior-max-char': number
  'text-field-behavior-input-type': string
  'text-field-behavior-required': boolean
  'text-field-behavior-password-mask': boolean
  'text-field-behavior-character-counter': boolean
}

export interface ITextFieldComponent extends IBuilderComponent {
  componentId: 'text-field'
  groupId: 'form'
  settings: ITextFieldComponentSettings
}

/**
 * Text Field AI Input
 * Note: columnId is required to map this field to a table column
 */
export interface AiTextFieldInput {
  columnId: string // Required: maps to table column
  label?: string[] // Format: ["text:Field Label"] or ["text:Enter your ", "profile:username"]
  helpText?: string[] // Format: ["text:Help text"] or ["text:This field is required"]
  prefix?: string[] // Format: ["text:Enter text here"] or ["text:Type ", "cr:fieldName"]
  suffix?: string[] // Format: ["text:Enter text here"] or ["text:Type ", "cr:fieldName"]
  defaultValue?: string // Default value in select format: "text:default" or "cr:columnId" or "profile:username"
  required?: boolean // Is this field required?
  inputType?: string // Input type: "text", "email", "password", "tel", etc.
  minChar?: number // Minimum characters
  maxChar?: number // Maximum characters
  pattern?: string // Validation pattern
  showHelp?: boolean // Show help text
  passwordMask?: boolean // Mask password input
  characterCounter?: boolean // Show character counter
}

const TEXT_FIELD_DEFAULT_TEMPLATE = textFieldTemplate as unknown as ITextFieldComponentSettings

export class TextFieldComp {
  getTemplate(): ITextFieldComponentSettings {
    return TEXT_FIELD_DEFAULT_TEMPLATE
  }

  /**
   * format text field component settings to AI text field input
   */
  format(settings: ITextFieldComponentSettings): AiTextFieldInput {
    const template = this.getTemplate()
    const result: AiTextFieldInput = {
      columnId: settings['text-field-data-value'] || '',
    }

    // Label
    const label = formatFromVariableText(settings['text-field-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Help Text
    const helpText = formatFromVariableText(settings['text-field-data-help'])
    if (helpText && helpText.length > 0) {
      result.helpText = helpText
    }

    // Prefix
    const prefix = formatFromVariableText(settings['text-field-data-prefix'])
    if (prefix && prefix.length > 0) {
      result.prefix = prefix
    }

    // Suffix
    const suffix = formatFromVariableText(settings['text-field-data-suffix'])
    if (suffix && suffix.length > 0) {
      result.suffix = suffix
    }

    // Default Value
    const defaultValue = formatFromVariableSelect(settings['text-field-data-default-value'])
    if (defaultValue) {
      result.defaultValue = defaultValue
    }

    // Required
    if (settings['text-field-behavior-required'] !== undefined) {
      result.required = settings['text-field-behavior-required']
    }

    // Input Type
    if (settings['text-field-behavior-input-type']) {
      result.inputType = settings['text-field-behavior-input-type']
    }

    // Min/Max Characters
    if (settings['text-field-behavior-min-char'] !== undefined) {
      result.minChar = settings['text-field-behavior-min-char']
    }
    if (settings['text-field-behavior-max-char'] !== undefined) {
      result.maxChar = settings['text-field-behavior-max-char']
    }

    // Pattern
    if (settings['text-field-behavior-pattern'] && settings['text-field-behavior-pattern'] !== 'none') {
      result.pattern = settings['text-field-behavior-pattern']
    }

    // Show Help
    if (settings['text-field-behavior-pattern-show-help'] !== undefined) {
      result.showHelp = settings['text-field-behavior-pattern-show-help']
    }

    // Password Mask
    if (settings['text-field-behavior-password-mask'] !== undefined) {
      result.passwordMask = settings['text-field-behavior-password-mask']
    }

    // Character Counter
    if (settings['text-field-behavior-character-counter'] !== undefined) {
      result.characterCounter = settings['text-field-behavior-character-counter']
    }

    return result
  }

  /**
   * parse AI text field input to text field component settings
   */
  parse(input: AiTextFieldInput): ITextFieldComponentSettings {
    const result = deepMerge(TEXT_FIELD_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['text-field-data-value'] = input.columnId
    }

    // Data - Label
    if (input.label) {
      result['text-field-data-label'] = parseToVariableText(input.label)
    }

    // Data - Default Value
    if (input.defaultValue !== undefined) {
      result['text-field-data-default-value'] = parseToVariableSelect(input.defaultValue)
    }

    // Data - Help Text
    if (input.helpText) {
      result['text-field-data-help'] = parseToVariableText(input.helpText)
    }

    // Data - Prefix
    if (input.prefix) {
      result['text-field-data-prefix'] = parseToVariableText(input.prefix)
    }

    // Data - Suffix
    if (input.suffix) {
      result['text-field-data-suffix'] = parseToVariableText(input.suffix)
    }

    // Behavior - Required
    if (input.required !== undefined) {
      result['text-field-behavior-required'] = input.required
    }

    // Behavior - Input Type
    if (input.inputType) {
      result['text-field-behavior-input-type'] = input.inputType
    }

    // Behavior - Min/Max Characters
    if (input.minChar !== undefined) {
      result['text-field-behavior-min-char'] = input.minChar
    }

    if (input.maxChar !== undefined) {
      result['text-field-behavior-max-char'] = input.maxChar
    }

    // Behavior - Pattern
    if (input.pattern) {
      result['text-field-behavior-pattern'] = input.pattern
    }

    if (input.showHelp !== undefined) {
      result['text-field-behavior-pattern-show-help'] = input.showHelp
    }

    // Behavior - Password Mask
    if (input.passwordMask !== undefined) {
      result['text-field-behavior-password-mask'] = input.passwordMask
    }

    // Behavior - Character Counter
    if (input.characterCounter !== undefined) {
      result['text-field-behavior-character-counter'] = input.characterCounter
    }

    return result
  }
}
